import React, { Component } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import { withRouter, Link } from 'react-router-dom';

import clsx from 'clsx';

import BadgeDisplay from 'components/BadgeDisplay';
import ContentHeader from 'components/ContentHeader';
import { FastTrackStatusBanner } from 'components/FastTrackStatusBanner/FastTrackStatusBanner';
import { FastTrackPopups } from 'components/FastTrackPopups/FastTrackPopups';
import SubAppButton from 'components/TrainingGrid/SubAppButton';
import TrainingCard from 'components/TrainingGrid/TrainingCard';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import PointsHandler from 'config/PointsHandler';
import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'config/settings.json';

import badges from 'assets/images/badges';
import getFeatureFlags from 'helpers/getFeatureFlags';
import { isRewardsCountry } from 'helpers/isRewardsCountry';
import { fastTrackData } from 'helpers/FastTrack/fastTrackData';
import { calculateGrade } from 'helpers/FastTrack/fastTrackGrade';
import { parseFastTrackGraduation } from 'helpers/FastTrack/parseFastTrackGraduation';

class TrainingGrid extends Component {
  constructor(props) {
    super(props);

    const fastTrackFinished = fastTrackData('finished') || false;
    const fastTrackErrors = +fastTrackData('errors') || 0;
    const fastTrackInProgress =
      !!fastTrackData('startTime') &&
      !fastTrackData('finished') &&
      !fastTrackData('canceled') &&
      +fastTrackData('errors') !== -1;

    this.state = {
      appId: 0,
      lang: 'en',
      email: '',
      chapters: [],
      chapters_progress: [],
      subApps: [],
      subAppsFull: [],
      completedSubApps: [],
      foundationCompleted: false,
      fastTrackCanceled: fastTrackData('canceled') || false,
      fastTrackErrors,
      fastTrackFinished,
      fastTrackInProgress,
      fastTrackGrade: { grade: '' },
      hasFastTrack: false,
      appName: '',
      featureFlags: [],
      foundationChapters: [],
      specializationChapters: [],
      // userCountry: '',
      isRewardsCountryEligible: false,
    };

    this.isLocked = this.isLocked.bind(this);
    this.loadChapters = this.loadChapters.bind(this);
    this.setSubApps = this.setSubApps.bind(this);
    this.calculateProgress = this.calculateProgress.bind(this);
  }

  componentDidMount() {
    console.log('TrainingGrid mounted');
    const { fastTrackErrors, fastTrackFinished } = this.state;

    this.mounted = true;

    this.getAppInfo();
    this.getProgress();

    if (fastTrackFinished && fastTrackErrors !== -1) {
      this.getGrade(fastTrackErrors);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  async getAppInfo() {
    const { code } = await StorageHandler.load(storageKeys.lang);
    // let lang = `${language}-${language.toUpperCase()}`;

    const appInfo = await StorageHandler.load(storageKeys.appInfo);
    if (appInfo) {
      const { appName, featureFlags } = appInfo;
      const resolvedFlags = await getFeatureFlags();
      const hasFastTrack = resolvedFlags.enable_fast_track && !!appInfo.fastTrackAppId;
      this.setState({ appName, lang: code, featureFlags, hasFastTrack });
    }
  }

  getProgress() {
    StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        const appId = import.meta.env.VITE_APP_ID;
        const { email, country } = res;

        if (this.mounted) {
          // this.setState({ appId, email, userCountry: country }); // Explicityly ommented instead of removed for potential reintroduction in Fasttrack.
          this.setState({ appId, email });
          isRewardsCountry(country).then((isRewardsCountryEligible) => {
            if (this.mounted) this.setState({ isRewardsCountryEligible });
          });
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            if (userProgress && userProgress.message && userProgress.message == 'App_ID not found') {
              APIHandler.registerUserProgress(email).then(() => {
                APIHandler.getUserProgress(appId, email).then((userReProgress) => {
                  this.calculateProgress(userReProgress);
                });
              });
            } else {
              this.calculateProgress(userProgress);
            }
          });
        }
      }
    });
  }

  getTieredValue(array = null) {
    const { tiers, totalPoints } = this.pointsHandler;

    let index = 0;
    tiers.forEach(({ points: tierPoints }, i) => {
      // Could be improved, maybe using filter / reduce?
      if (totalPoints >= tierPoints) {
        index = i;
      }
    });

    if (array) return array[index < 0 ? 0 : index];
    return index;
  }

  async getGrade() {
    const correctAnswers = [...new Set(fastTrackData('correctAnswers') ?? [])];
    const totalQuestions = fastTrackData('totalQuestions') ?? 0;
    const result = calculateGrade(correctAnswers, totalQuestions);

    const appId = await DataHandler.matchAppId();
    const user = await StorageHandler.load(storageKeys.user);

    if (user) {
      const startTime = fastTrackData('startTime');
      const total = Math.floor((Date.now() - (startTime || 0)) / 1000);

      fastTrackData('totalTime', total);

      const accessToken = await StorageHandler.load(storageKeys.accessToken);

      try {
        const raw = await APIHandler.fastTrackGraduation(appId, accessToken, result.letterGrade, total);
        const parsed = parseFastTrackGraduation(raw);

        if (parsed.status === 'success' && parsed.trainingGraduated) {
          fastTrackData('trainingGraduated', parsed.trainingGraduated);
        }

        this.setState({
          fastTrackGrade: {
            grade: parsed.grade,
            passed: parsed.status === 'success',
          },
        });
        return;
      } catch {
        // fallback to locally computed grade
      }
    }

    this.setState({
      fastTrackGrade: { grade: result.letterGrade },
    });
  }

  getSubAppProgress(appIds) {
    const { email } = this.state;
    if (this.mounted && appIds.length > 0) {
      return APIHandler.getUserProgress(appIds, email).then((userProgress) => {
        const progress = {};
        Object.values(userProgress.data).map((app) => {
          const { chapters } = app;

          if (chapters.length === 1) {
            progress[app.app_id] = Math.min(
              (chapters.reduce((sum, chapter) => sum + chapter.progress, 0) /
                chapters.reduce((sum, chapter) => sum + (chapter.total_pages - 1), 0)) *
                100,
              100,
            );
          }
          if (chapters.length > 1) {
            progress[app.app_id] = Math.min(
              (chapters
                .filter((chapter) => chapter.specialization === 0)
                .reduce((sum, chapter) => sum + chapter.progress, 0) /
                chapters
                  .filter((chapter) => chapter.specialization === 0)
                  .reduce((sum, chapter) => sum + chapter.total_pages, 0)) *
                100,
              100,
            );
          }
        });

        Object.keys(progress).map((app) => {
          if (progress[app] === 100) {
            this.setState({ completedSubApps: app });
          }
        });
        return progress;
      });
    }
  }

  setSubApps() {
    const { appId, email, lang, foundationCompleted } = this.state;
    APIHandler.getSubappsNew(appId, email, lang).then(async ({ categories }) => {
      if (!categories) return;
      const subIds = [];
      const allSubApps = categories
        .map((app) => {
          return app?.micro_modules?.map((app) => app);
        })
        .flat();
      allSubApps.map(({ sub_app }) => subIds.push(sub_app?.id));

      const fullSub = allSubApps.filter(({ sub_app }) => {
        return sub_app.level === 'iii';
      });

      const microSub = allSubApps.filter(({ sub_app }) => {
        return sub_app.level === 'ii' || sub_app.level === 'ii_partner';
      });

      const progressPartners = await this.getSubAppProgress(subIds, email);

      const microTiles = microSub.map((app) => {
        return (
          <SubAppButton
            key={`${app.id}_${app.chapterImage}`}
            progress={progressPartners?.[app.sub_app.id] ?? 0}
            appId={app.sub_app.id}
            name={app.title}
            pageId={0}
            size={{ height: 50, width: 50 }}
            type="training"
            level="ii"
            backgroundImage={app.image || app.chapterImage}
            src={app.sub_app.logo_wide_white}
            numChapters={1}
            uri={app.sub_app.website_url}
            email={email}
            chapterId={app.count.id}
            locked={!foundationCompleted}
          />
        );
      });
      const fullTiles = fullSub.map((app) => {
        return (
          <SubAppButton
            key={`${app.id}_${app.chapterImage}`}
            progress={progressPartners?.[app.sub_app.id]}
            appId={app.sub_app.id}
            name={app.title}
            pageId={0}
            size={{ height: 50, width: 50 }}
            type="training"
            level={app.sub_app.level}
            backgroundImage={app.background_image || app.image}
            src={app.sub_app.logo_wide_white}
            numChapters={app.count.pages_count}
            uri={app.sub_app.website_url}
            email={email}
            chapterId={app.count.id}
            locked={!foundationCompleted}
          />
        );
      });

      this.setState({ subAppsFull: fullTiles, subApps: microTiles });
    });
  }

  async handlePoints() {
    const {
      //   chapters_progress: progress,
      chapters,
      completedSubApps,
      foundationChapters,
      specializationChapters,
    } = this.state;

    const intro = foundationChapters.filter(({ percentage }) => percentage && percentage >= 100);

    const rest = specializationChapters.filter(({ percentage }) => percentage && percentage >= 100);

    if (chapters && completedSubApps && this.pointsHandler.tiers) {
      const introsComplete = intro.map(({ chapterID }) => this.updatePoints('intro-chapter', chapterID));
      const chaptersComplete = rest.map(({ chapterID }) => this.updatePoints('chapter', chapterID));
      const subAppsComplete = completedSubApps?.map((chapterID) => this.updatePoints('subapp', chapterID));

      const allItemsCompleted = [introsComplete.length, chaptersComplete.length, subAppsComplete?.length];
      const allItems = [chapters.props.children.length, completedSubApps?.length];

      const totalItemCountCompleted = allItemsCompleted.reduce((total, len) => total + len);

      const totalItemCount = allItems.reduce((total, len) => total + len);

      this.setState({ totalItemCountCompleted, totalItemCount });
    }
  }

  async updatePoints(action, i) {
    const { totalPoints, tiers } = this.pointsHandler;

    // Check if user is maxed out:
    // - New tier system: check if all tiers have 'achieved' flag set to true
    // - Legacy system: check if totalPoints >= highest tier's points threshold
    const hasNewTierSystem = tiers.some((tier) => 'achieved' in tier);
    const userIsMaxed = hasNewTierSystem
      ? tiers.every((tier) => tier.achieved === true)
      : totalPoints >= (tiers[tiers.length - 1]?.points || 0);

    if (!userIsMaxed) {
      const { response } = await this.pointsHandler.setPoints({
        action,
        value: `${action}-${i}`,
      });

      if (response !== 'Already used') {
        const { update } = response;

        return update;
      }
    }
    return { points: 0 };
  }

  async initPointsHandler() {
    this.pointsHandler = new PointsHandler();
    await this.pointsHandler.init();
    await this.pointsHandler.getPoints();
  }

  loadChapters() {
    const { appId, email, lang, foundationCompleted, featureFlags } = this.state;
    APIHandler.getChaptersAndProgress(appId, email, lang).then(({ chapters }) => {
      const allChapters = chapters?.uncategorized?.[0]?.chapters ?? [];
      const visibleChapters = allChapters.filter(
        (chapter) => chapter.coming_soon !== true || chapter.coming_soon_visible === true,
      );
      const foundationChapters = visibleChapters.filter((chapter) => chapter.specialization === 0);
      const specializationChapters = visibleChapters.filter((chapter) => chapter.specialization === 1);

      const isFoundationalSpan3 = foundationChapters.length % 2 === 0 && foundationChapters.length % 3 !== 0;

      // Backup: if the progress-sync request lags/fails, derive completion from the list we render.
      const isChapterComplete = (c) => {
        const total = Number(c?.total_pages ?? 0);
        if (total > 0) {
          const threshold = Math.max(0, total - 1);
          const progressVal = Number(c?.progress ?? 0);
          const lastPage = Number(c?.last_page ?? 0);
          const pct = Number(c?.percentage ?? 0);
          return progressVal >= threshold || lastPage >= threshold || pct >= 100;
        }
        return Number(c?.percentage ?? 0) >= 100;
      };

      const foundationCompletedFromChapters =
        foundationChapters.length > 0 ? foundationChapters.every((c) => isChapterComplete(c)) : false;

      const effectiveFoundationCompleted = foundationCompleted || foundationCompletedFromChapters;
      const reviewCompletedInRetain = featureFlags.some((flag) => flag.name === 'smart_assist');

      const newChapters = (
        <React.Fragment key={effectiveFoundationCompleted}>
          <span className="subHeader">
            <ContentHeader
              header={i18n.translate('contentHeaders.maintrainings')}
              subtitle={i18n.translate('pageDescriptions.maintrainings')}
              line
            />
          </span>
          {foundationChapters.map((chapter, key, all) => {
            const testId = key === all.length - 1 ? 'training-grid-chapter-last' : `training-grid-chapter-${key}`;

            return (
              <div
                key={key}
                data-testid={testId}
                className={clsx('trainingCard__Animated', {
                  'trainingCard__Animated--span3': isFoundationalSpan3,
                })}
              >
                <TrainingCard
                  chaptersProgress={chapter}
                  progress={chapter.percentage}
                  chapter={chapter}
                  chapterCount={chapter.chapterItemCount}
                  chapterCountMin={chapter.chapterItemCountMin}
                  locked={this.isLocked(key) || chapter.coming_soon_visible}
                  index={key}
                  appId={appId}
                  key={chapter.chapterID}
                  foundation={true}
                  comingSoon={chapter.coming_soon_visible}
                  reviewCompletedInRetain={reviewCompletedInRetain}
                />
              </div>
            );
          })}
          {specializationChapters.length > 0 && (
            <span className="subHeader">
              <ContentHeader
                header={i18n.translate('contentHeaders.subtrainings')}
                subtitle={i18n.translate('pageDescriptions.subtrainings')}
                line
              />
            </span>
          )}
          {specializationChapters.map((chapter, key) => {
            return (
              <div
                key={key}
                className={clsx('trainingCard__Animated', {
                  specialization: true,
                })}
              >
                <TrainingCard
                  chaptersProgress={chapter}
                  chapter={chapter}
                  progress={chapter.percentage}
                  chapterCount={chapter.chapterItemCount}
                  chapterCountMin={chapter.chapterItemCountMin}
                  locked={!effectiveFoundationCompleted || chapter.coming_soon_visible}
                  index={key}
                  appId={appId}
                  key={chapter.chapterID}
                  foundation={false}
                  comingSoon={chapter.coming_soon_visible}
                  reviewCompletedInRetain={reviewCompletedInRetain}
                />
              </div>
            );
          })}
        </React.Fragment>
      );
      this.setState(
        {
          foundationChapters,
          specializationChapters,
          foundationCompleted: effectiveFoundationCompleted,
          chapters: newChapters,
        },
        async () => {
          await this.initPointsHandler();
          await this.handlePoints();
          this.setSubApps();

          // await this.setSubApps(appId);
        },
      );
    });
  }

  calculateProgress(res) {
    if (res.status !== 'error') {
      console.log(res);
      const { chapters } = res.data[0];

      chapters?.forEach((chapter) => {
        const newChapter = chapter;
        if (chapter.total_pages > 0) {
          const denom = Math.max(1, chapter.total_pages - 1);
          newChapter.percentage = Math.floor(((chapter.progress || 0) / denom) * 100);
          if (newChapter.percentage > 100) {
            newChapter.percentage = 100;
          }
          // Bug fix for last_page higher than progress
          if (chapter.last_page && Math.floor((chapter.total_pages / chapter.last_page) * 100) === 100) {
            newChapter.percentage = 100;
          }
        }
      });
      const isChapterComplete = (c) => {
        const total = Number(c?.total_pages ?? 0);
        if (total <= 0) return false;
        const threshold = Math.max(0, total - 1);
        const progressVal = Number(c?.progress ?? 0);
        const lastPage = Number(c?.last_page ?? 0);
        const pct = Number(c?.percentage ?? 0);
        return progressVal >= threshold || lastPage >= threshold || pct >= 100;
      };

      const visibleChapters = chapters.filter((c) => c.coming_soon !== true || c.coming_soon_visible === true);
      const foundationChapters = visibleChapters.filter((c) => !!c.specialization === false);
      const foundationCompleted =
        foundationChapters.length > 0 ? foundationChapters.every((c) => isChapterComplete(c)) : false;

      this.setState({ chapters_progress: visibleChapters, foundationCompleted }, () => {
        this.loadChapters(true);
      });
      return;
    }
    this.loadChapters(false);
  }

  isLocked(key) {
    const { chapters_progress: progress, featureFlags } = this.state;
    // If specialization chapter, lock if not all foundation chapters are completed, otherwise unlock
    if (!!progress[key]?.specialization === true || key === undefined) {
      const foundationChapters = progress.filter((chapter) => !!chapter.specialization === false);
      const foundationChaptersCompleted = foundationChapters.filter((chapter) => chapter.percentage >= 100);

      if (foundationChaptersCompleted.length === foundationChapters.length) {
        this.setState({ foundationCompleted: true });

        return false;
      }
      return true;
    }

    const unlockAllChaptersAfterFirst = featureFlags.some((flag) => flag.name === 'unlock_all_chapters_after_first');
    if (unlockAllChaptersAfterFirst) {
      // Only the first chapter starts locked; once it's completed, every other foundation chapter unlocks.
      return key !== 0 && (progress.length === 0 || progress[0].percentage !== 100);
    }

    // Lock chapter if it's not the first chapter and the previous chapter isn't completed
    if (progress.length > 0 && key !== 0 && progress[key - 1].percentage !== 100) {
      return true;
    }
    // Unlock chapter if it's the first chapter or the previous chapter is completed
    if (progress.length > 0 && (key === 0 || progress[key - 1].percentage >= 100)) {
      return false;
    }
    return true;
  }

  render() {
    const { history } = this.props;
    const {
      chapters,
      subApps,
      subAppsFull,
      fastTrackFinished,
      fastTrackErrors,
      fastTrackCanceled,
      fastTrackGrade,
      fastTrackInProgress,
      chapters_progress,
      featureFlags,
      hasFastTrack,
      // userCountry,
      isRewardsCountryEligible,
    } = this.state;

    const hasNoFoundationProgress =
      chapters_progress.length > 0 &&
      chapters_progress.filter((ch) => ch.specialization === 0).every((ch) => !ch.progress);

    const { tierBadges } = badges;
    const { initialized, tiers, totalPoints } = this.pointsHandler || {};

    // Check if using new tier system (tiers have 'achieved' property)
    // If no tier is achieved yet, user is unranked - set tierIndex to null
    const hasNewTierSystem = tiers && tiers.some((tier) => 'achieved' in tier);
    const isUnranked = hasNewTierSystem && !tiers.some((tier) => tier.achieved === true);
    const tierIndex = isUnranked ? null : tiers && Math.min(this.getTieredValue(), tiers.length - 1);

    return (
      <div>
        {featureFlags.some((flag) => flag.name === 'metallic_tiers') &&
          isRewardsCountryEligible &&
          initialized &&
          tiers && (
            <Link to="/main/rewards/achievements" className="achievementsBtn">
              <BadgeDisplay
                tierBadges={tierBadges}
                tierIndex={tierIndex}
                tiers={tiers}
                totalPoints={totalPoints}
                layout="pillar"
                position="right"
                badgeSubtitleOverride={i18n.translate('badgeDisplay.status')}
              />
            </Link>
          )}
        {hasFastTrack && fastTrackInProgress && (
          <FastTrackStatusBanner
            answered={fastTrackData('answered') || 0}
            onAbandon={() => {
              fastTrackData('canceled', true);
              this.setState({ fastTrackInProgress: false, fastTrackCanceled: true });
            }}
          />
        )}
        {chapters && subApps && (
          <div>
            <div data-testid="training-grid-chapters" className={clsx('trainingGrid')}>
              {chapters}
            </div>
            {subApps.length > 0 && (
              <React.Fragment>
                <div className={clsx('trainingGrid', 'subApps')}>
                  <span className="subHeader">
                    <ContentHeader
                      header={i18n.translate('contentHeaders.subapps')}
                      subtitle={i18n.translate('pageDescriptions.subapps')}
                      line
                    />
                  </span>
                  {subApps}
                </div>
              </React.Fragment>
            )}
            {subAppsFull.length > 0 && (
              <React.Fragment>
                <div className={clsx('trainingGrid', 'subAppsFull')}>
                  <span className="subHeader">
                    <ContentHeader
                      header={i18n.translate('contentHeaders.subAppsFull')}
                      subtitle={i18n.translate('pageDescriptions.subAppsFull')}
                      line
                    />
                  </span>
                  {subAppsFull}
                </div>
              </React.Fragment>
            )}
          </div>
        )}
        {import.meta.env.MODE !== 'test' && (
          <FastTrackPopups
            fastTrackCanceled={fastTrackCanceled}
            fastTrackErrors={+fastTrackErrors}
            fastTrackFinished={fastTrackFinished}
            fastTrackInProgress={fastTrackInProgress}
            fastTrackGrade={fastTrackGrade}
            hasNoFoundationProgress={hasNoFoundationProgress}
            hasFastTrack={hasFastTrack}
            onConfirmAsk={() => {
              this.setState({ fastTrackErrors: '0' }, () => {
                history.push('/main/fasttrack/chapter/0/0');
              });
            }}
            onCancelAsk={() => {
              fastTrackData('canceled', true);
              this.setState({ fastTrackCanceled: true });
            }}
            onConfirmGrade={() => {
              const { grade } = fastTrackGrade || {};
              fastTrackData('errors', -1);
              fastTrackData('grade', grade);
              this.setState({ fastTrackFinished: true, fastTrackErrors: -1 });
            }}
          />
        )}
      </div>
    );
  }
}

TrainingGrid.propTypes = {
  history: PropTypes.shape().isRequired,
};

export default withRouter(TrainingGrid);
